import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { LogOut, Plus, ChevronRight, Crown, Trash2, RotateCcw, Archive } from "lucide-react";
import { toast } from "sonner";
import { ThemeToggle } from "@/components/ThemeToggle";

export default function AdminDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", character_name: "", kingdom_name: "" });
  const [saving, setSaving] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [deleted, setDeleted] = useState([]);

  const load = () => {
    api.get("/players").then(({ data }) => setPlayers(data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const loadDeleted = () => api.get("/players-deleted").then(({ data }) => setDeleted(data));

  const create = async () => {
    if (!form.email) { toast.error("Username is required"); return; }
    setSaving(true);
    try {
      await api.post("/players", form);
      toast.success("Player account created");
      setOpen(false);
      setForm({ email: "", character_name: "", kingdom_name: "" });
      setLoading(true);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const softDelete = async (e, p) => {
    e.stopPropagation();
    try {
      await api.delete(`/players/${p.id}`);
      toast.success(`Moved "${p.kingdom_name}" to trash`);
      setPlayers((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const restore = async (p) => {
    await api.post(`/players/${p.id}/restore`);
    toast.success(`Restored "${p.kingdom_name}"`);
    setDeleted((prev) => prev.filter((x) => x.id !== p.id));
    setLoading(true); load();
  };

  const permaDelete = async (p) => {
    if (!window.confirm(`Permanently delete "${p.kingdom_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/players/${p.id}/permanent`);
      toast.success("Permanently deleted");
      setDeleted((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-gold/25 sticky top-0 bg-background/85 backdrop-blur-md z-10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo className="h-8 w-auto object-contain" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={logout} className="rounded-sm" data-testid="logout-button">
              <LogOut strokeWidth={1.5} className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 ht-fade-up">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="ht-eyebrow mb-3">Game Master</div>
            <h1 className="ht-title text-4xl sm:text-5xl">Player Accounts</h1>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm font-heading" data-testid="create-account-button">
                <Plus strokeWidth={1.5} className="w-4 h-4 mr-2" /> Create Account
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm bg-card max-w-md">
              <DialogHeader><DialogTitle className="font-heading">New Player Account</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {[
                  ["email", "Username", "text"],
                  ["character_name", "Character Name", "text"],
                  ["kingdom_name", "Faction", "text"],
                ].map(([k, label, type]) => (
                  <div key={k}>
                    <label className="ht-uppercase-label block mb-1.5">{label}</label>
                    <Input type={type} value={form[k]} onChange={upd(k)} className="rounded-sm bg-background" data-testid={`create-${k}-input`} />
                  </div>
                ))}
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={saving} className="rounded-sm w-full font-heading" data-testid="submit-create-account">
                  {saving ? "Creating…" : "Create Account"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="ht-uppercase-label animate-pulse">Loading players…</p>
        ) : players.length === 0 ? (
          <div className="ht-panel p-12 text-center text-muted-foreground">
            No player accounts yet. Create the first one.
          </div>
        ) : (
          <div className="ht-panel divide-y divide-gold/15" data-testid="players-list">
            {players.map((p) => (
              <div
                key={p.id}
                data-testid={`player-row-${p.id}`}
                onClick={() => navigate(`/admin/players/${p.id}`)}
                className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gold/5 transition-colors group cursor-pointer"
              >
                <div className="w-11 h-11 border border-gold/40 rounded-sm flex items-center justify-center shrink-0">
                  <Crown strokeWidth={1.5} className="w-5 h-5 text-gold" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="ht-title text-lg truncate">{p.kingdom_name || "Unnamed Faction"}</div>
                  <div className="text-sm text-muted-foreground truncate">
                    {p.character_name || "—"} · {p.email}
                  </div>
                </div>
                <Button variant="ghost" size="icon"
                  className="rounded-sm h-9 w-9 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => softDelete(e, p)} data-testid={`delete-player-${p.id}`}>
                  <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                </Button>
                <ChevronRight strokeWidth={1.5} className="w-5 h-5 text-gold group-hover:translate-x-1 transition-transform" />
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Trash bin — out of the way, bottom-right */}
      <Dialog open={trashOpen} onOpenChange={(o) => { setTrashOpen(o); if (o) loadDeleted(); }}>
        <DialogTrigger asChild>
          <Button variant="outline" size="icon"
            className="fixed bottom-6 left-6 rounded-full h-11 w-11 border-gold/40 bg-card/90 backdrop-blur shadow-lg z-40"
            title="Deleted profiles" data-testid="open-trash">
            <Archive strokeWidth={1.5} className="w-5 h-5" />
          </Button>
        </DialogTrigger>
        <DialogContent className="rounded-sm bg-card max-w-lg">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Trash2 strokeWidth={1.5} className="w-5 h-5" /> Deleted Profiles</DialogTitle></DialogHeader>
          {deleted.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="trash-empty">The trash is empty.</p>
          ) : (
            <div className="ht-panel divide-y divide-gold/15" data-testid="deleted-list">
              {deleted.map((p) => (
                <div key={p.id} className="flex items-center gap-3 px-4 py-3" data-testid={`deleted-row-${p.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="ht-title text-base truncate">{p.kingdom_name || "Unnamed Faction"}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.character_name || "—"} · {p.email}</div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-sm" onClick={() => restore(p)} data-testid={`restore-${p.id}`}>
                    <RotateCcw strokeWidth={1.5} className="w-4 h-4 mr-1" /> Restore
                  </Button>
                  <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8 text-destructive" onClick={() => permaDelete(p)} data-testid={`permadelete-${p.id}`}>
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
